import TimeIcons from '@/icons/TimeIcons'
import { Contract } from '@/modules/Contract/types'
import { Plus, Trash2 } from 'lucide-react'
import { Path } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Button, GridHookForm, Typogaphy } from 'vnetwork-ui'
import { useOtherStandardTemplate } from './useOtherStandardTemplate'
import { dateValue, formatPriceInput, parsePriceInput } from './utils'

interface OtherStandardProps {
    index: number
    isPOC?: boolean
}

const OtherStandard: React.FC<OtherStandardProps> = ({
    index,
    isPOC = false,
}) => {
    const { t } = useTranslation('contract')
    const {
        formControl,
        root,
        paymentMode,
        ranges,
        updateRange,
        addRange,
        removeRange,
    } = useOtherStandardTemplate(index)

    if (isPOC) return null

    return (
        <div className="space-y-4">
            <GridHookForm
                subForm
                actions={[]}
                formControl={formControl}
                data={[
                    {
                        formItemProps: {
                            name: `${root}.payment_method` as Path<Contract>,
                            type: 'select',
                            label: t('other_standard.payment_method'),
                            placeholder: t('other_standard.payment_method'),
                            options: [
                                {
                                    label: t('other_standard.prepaid'),
                                    value: 'prepaid',
                                },
                                {
                                    label: t('other_standard.postpaid'),
                                    value: 'postpaid',
                                },
                            ],
                            allowClear: false,
                        },
                        className: 'col-span-2',
                    },
                    {
                        formItemProps: {
                            name: `${root}.description` as Path<Contract>,
                            type: 'textarea',
                            label: t('other_standard.description'),
                            placeholder: t('other_standard.description'),
                        },
                        className: 'col-span-4',
                    },
                ]}
            />

            <div className="bg-white rounded border border-slate-200">
                <div className="px-3 py-2.5 bg-purple-50/50 border-b border-purple-200/60">
                    <div className="flex items-center gap-2.5">
                        <div className="w-6 h-6 bg-purple-100 rounded flex items-center justify-center flex-shrink-0">
                            <TimeIcons className="w-3.5 h-3.5 text-purple-600" />
                        </div>
                        <Typogaphy className="font-medium text-purple-800 text-sm">
                            {t('service-time')}
                        </Typogaphy>
                    </div>
                </div>

                <div className="p-3 space-y-3">
                    <GridHookForm
                        subForm
                        actions={[]}
                        formControl={formControl}
                        data={[
                            {
                                formItemProps: {
                                    name: `${root}.payment_mode` as Path<Contract>,
                                    type: 'select',
                                    label: t('other_standard.payment_mode'),
                                    placeholder: t(
                                        'other_standard.payment_mode'
                                    ),
                                    options: [
                                        {
                                            label: t(
                                                'other_standard.pay_one_time'
                                            ),
                                            value: 'one_time',
                                        },
                                        {
                                            label: t(
                                                'other_standard.pay_multiple_times'
                                            ),
                                            value: 'multiple_times',
                                        },
                                    ],
                                    allowClear: false,
                                },
                                className: 'col-span-4',
                            },
                        ]}
                    />

                    {ranges.map((range, rangeIndex) => (
                        <div
                            key={rangeIndex}
                            className="border border-slate-200 rounded p-3 space-y-3"
                        >
                            <div className="flex items-center justify-between gap-3">
                                <Typogaphy className="font-medium text-slate-800 text-sm">
                                    {t('other_standard.payment_range')}{' '}
                                    {rangeIndex + 1}
                                </Typogaphy>
                                {paymentMode === 'multiple_times' &&
                                    ranges.length > 1 && (
                                        <Button
                                            type="button"
                                            variant="outlined"
                                            color="error"
                                            size="small"
                                            startIcon={
                                                <Trash2 className="w-4 h-4" />
                                            }
                                            onClick={() =>
                                                removeRange(rangeIndex)
                                            }
                                        >
                                            {t('delete')}
                                        </Button>
                                    )}
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                <label className="space-y-1">
                                    <span className="text-xs text-slate-600">
                                        {t('first_startdate')}
                                    </span>
                                    <input
                                        className="w-full h-9 rounded border border-slate-200 px-3 text-sm"
                                        type="date"
                                        value={dateValue(range.start_date)}
                                        onChange={(event) =>
                                            updateRange(
                                                rangeIndex,
                                                'start_date',
                                                event.target.value
                                            )
                                        }
                                    />
                                </label>
                                <label className="space-y-1">
                                    <span className="text-xs text-slate-600">
                                        {t('first_enddate')}
                                    </span>
                                    <input
                                        className="w-full h-9 rounded border border-slate-200 px-3 text-sm"
                                        type="date"
                                        value={dateValue(range.end_date)}
                                        onChange={(event) =>
                                            updateRange(
                                                rangeIndex,
                                                'end_date',
                                                event.target.value
                                            )
                                        }
                                    />
                                </label>
                                <label className="space-y-1">
                                    <span className="text-xs text-slate-600">
                                        {t('service_price_register')}
                                    </span>
                                    <input
                                        className="w-full h-9 rounded border border-slate-200 px-3 text-sm"
                                        type="text"
                                        inputMode="numeric"
                                        value={formatPriceInput(
                                            Number(range.price || 0)
                                        )}
                                        onChange={(event) =>
                                            updateRange(
                                                rangeIndex,
                                                'price',
                                                parsePriceInput(
                                                    event.target.value
                                                )
                                            )
                                        }
                                    />
                                </label>
                            </div>
                        </div>
                    ))}

                    {paymentMode === 'multiple_times' && (
                        <Button
                            type="button"
                            variant="outlined"
                            size="small"
                            startIcon={<Plus className="w-4 h-4" />}
                            onClick={addRange}
                        >
                            {t('other_standard.add_payment_range')}
                        </Button>
                    )}
                </div>
            </div>
        </div>
    )
}

export default OtherStandard
